/** Campaign status options */
export const CLAIM_CAMPAIGN_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'EXPIRED'] as const;

/** Campaign type - NORMAL for code/API-validated campaigns, MATCH for eligibility-matched campaigns */
export const CLAIM_CAMPAIGN_TYPES = ['NORMAL', 'MATCH'] as const;

/** Validator types for claim campaigns */
export const CLAIM_VALIDATOR_TYPES = ['PREGEN_CODE', 'SHARED_CODE', 'EXTERNAL_API', 'DIRECT', 'ADMIN_ONLY'] as const;

/** Campaign emails that can be turned off per campaign (`suppressedEmails`) */
export const CLAIM_CAMPAIGN_EMAILS = ['ENROLLMENT', 'REMINDER', 'DENIAL'] as const;

/** Match check types */
export const CLAIM_MATCH_CHECK_TYPES = ['BQ_QUERY'] as const;

/** Evidence field type options for the admin UI */
export const EVIDENCE_FIELD_TYPES = ['text', 'dropdown', 'file'] as const;

/** Action types that can be configured for a campaign */
export const CLAIM_ACTION_TYPES = ['TIER_UPGRADE', 'BONUS_POINTS', 'STATUS_CREDITS', 'DYNAMIC_TIER_UPGRADE'] as const;

/** Structured deny reasons used when rejecting a CS approval request. Kept in sync with the BE. */
export const DENY_REASONS = [
  'INSUFFICIENT_EVIDENCE',
  'INVALID_EVIDENCE',
  'NOT_ELIGIBLE',
  'EXPIRED_DOCUMENT',
  'DUPLICATE_SUBMISSION',
  'OTHER',
] as const;

type DenyReason = (typeof DENY_REASONS)[number];

/** The "Other" deny reason requires free-text notes to explain. */
export const isOtherReason = (reason: DenyReason | '' | null | undefined): boolean => reason === 'OTHER';

/** Allowed range for the `maxAppeals` field on a claim campaign. */
export const MIN_APPEALS = 0;
export const MAX_APPEALS = 10;

export const DENY_REASON_LABELS: Record<DenyReason, string> = {
  INSUFFICIENT_EVIDENCE: 'Insufficient evidence provided',
  INVALID_EVIDENCE: 'Evidence could not be verified',
  NOT_ELIGIBLE: 'Not eligible for this campaign',
  EXPIRED_DOCUMENT: 'Evidence document is expired',
  DUPLICATE_SUBMISSION: 'Duplicate submission',
  OTHER: 'Other (see notes)',
};

/** Safely resolves a deny-reason label. Falls back to the raw value for forward-compatibility. */
export function getDenyReasonLabel(reason: string | null | undefined): string {
  if (!reason) {
    return '';
  }
  return DENY_REASON_LABELS[reason as DenyReason] ?? reason;
}

/** Code status options */
export const CLAIM_CODE_STATUSES = ['AVAILABLE', 'CLAIMED', 'EXPIRED', 'REVOKED'] as const;

/** Claim record status options */
export const CLAIM_RECORD_STATUSES = [
  'PENDING',
  'VALIDATED',
  'COMPLETED',
  'PARTIAL_FAILED',
  'FAILED',
  'REJECTED',
] as const;

/** Claim action execution status */
export const CLAIM_ACTION_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'SKIPPED'] as const;

/** Target tier options for TIER_UPGRADE actions */
export const CLAIM_TARGET_TIERS = ['silver', 'gold', 'platinum'] as const;

/** HTTP methods for EXTERNAL_API validator */
export const EXTERNAL_API_METHODS = ['GET', 'POST'] as const;
